import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Cloud, Search, Download, X } from 'lucide-react';
import { z } from 'zod';
import { api } from '@/api/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { FilterChip } from '@/components/ui/filter-chip';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { DataTable, type ColumnDef, type RowSelectionState } from '@/components/ui/data-table';
import { taxYearLabel } from '@/lib/utils';
import type {
  AdminTaxReturn,
  AdminModuleAdminTaxReturnsResponse,
} from '@/api/generated/types.gen';

const searchSchema = z.object({
  status: z.enum(['draft', 'lodged', 'lodgefailed']).optional(),
  filter: z.enum(['needs-attention']).optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute('/admin/returns/')({
  validateSearch: searchSchema,
  component: AdminReturns,
});

type StatusKey = 'draft' | 'lodged' | 'lodgefailed';
const STATUSES: StatusKey[] = ['draft', 'lodged', 'lodgefailed'];

function AdminReturns() {
  const search = useSearch({ from: '/admin/returns/' });
  const navigate = Route.useNavigate();

  // The dashboard's overflow link sets ?filter=needs-attention, which we translate
  // into an initial status=lodgefailed filter (the highest-severity triage bucket).
  useEffect(() => {
    if (search.filter === 'needs-attention' && !search.status) {
      navigate({ search: { status: 'lodgefailed' }, replace: true });
    }
  }, [search.filter, search.status, navigate]);

  const [q, setQ] = useState(search.q ?? '');
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const status = search.status;

  const queryString = useMemo(() => {
    const p = new URLSearchParams({ pageNumber: '1', pageSize: '100' });
    if (status) p.set('status', status);
    if (debouncedQ.trim()) p.set('q', debouncedQ.trim());
    return p.toString();
  }, [status, debouncedQ]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'returns', queryString],
    queryFn: () => api<AdminModuleAdminTaxReturnsResponse>(`/admin/returns?${queryString}`),
    retry: false,
  });

  const items: AdminTaxReturn[] = useMemo(() => data?.items ?? [], [data]);

  // Reset the selection whenever the visible set changes (filter/search/refetch).
  useEffect(() => {
    setRowSelection({});
  }, [queryString]);

  const columns = useMemo<ColumnDef<AdminTaxReturn>[]>(
    () => [
      {
        accessorKey: 'taxpayerName',
        header: 'Taxpayer',
        cell: ({ row }) => (
          <span className="font-medium text-(--color-ink)">
            {row.original.taxpayerName ?? `Return #${row.original.id}`}
          </span>
        ),
      },
      {
        accessorKey: 'year',
        header: 'Year',
        cell: ({ row }) => (
          <span className="text-(--color-ink-2) tabular-nums">
            FY {taxYearLabel(Number(row.original.year))}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <ReturnStatusPill status={row.original.status ?? 'draft'} />,
      },
      {
        id: 'signals',
        header: 'Signals',
        enableSorting: false,
        cell: ({ row }) => (
          <Signals
            openConcernCount={Number(row.original.openConcernCount ?? 0)}
            prefillStatus={row.original.latestPrefillStatus ?? null}
          />
        ),
      },
      {
        accessorKey: 'lastModified',
        header: 'Last activity',
        cell: ({ row }) => (
          <span className="text-[13px] text-(--color-ink-muted) tabular-nums">
            {new Date(row.original.lastModified).toLocaleDateString('en-AU')}
          </span>
        ),
      },
    ],
    [],
  );

  const selectedCount = Object.keys(rowSelection).length;

  const exportCsv = () => {
    const rows = items.filter((r) => rowSelection[String(r.id)]);
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [['Taxpayer', 'Year', 'Status', 'Last activity'].join(',')];
    for (const r of rows) {
      lines.push(
        [
          r.taxpayerName ?? `Return #${r.id}`,
          `FY ${taxYearLabel(Number(r.year))}`,
          prettyStatus(r.status ?? 'draft'),
          new Date(r.lastModified).toLocaleDateString('en-AU'),
        ]
          .map((v) => escape(String(v)))
          .join(','),
      );
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `returns-${rows.length}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Reviewer console"
        title="All returns"
        description="Every return across the practice. Click any row for the read-only review view."
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-60 flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-ink-muted)" />
          <Input
            placeholder="Search taxpayer name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-1.5">
          <FilterChip
            label="All"
            active={!status}
            onClick={() => navigate({ search: (prev) => ({ ...prev, status: undefined }) })}
          />
          {STATUSES.map((s) => (
            <FilterChip
              key={s}
              label={prettyStatus(s)}
              active={status === s}
              onClick={() => navigate({ search: (prev) => ({ ...prev, status: s }) })}
            />
          ))}
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-(--radius) bg-(--color-navy-soft) px-4 py-2.5">
          <span className="text-[13px] font-semibold text-(--color-navy)">
            {selectedCount} selected
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={exportCsv}>
              <Download className="size-3.5" />
              Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>
              <X className="size-3.5" />
              Clear
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        loadingMessage="Loading returns…"
        emptyMessage={isError ? 'Could not load returns.' : 'No returns match the current filters.'}
        getRowId={(r) => String(r.id)}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        onRowClick={(r) => navigate({ to: '/admin/returns/$id', params: { id: String(r.id) } })}
        initialSorting={[{ id: 'lastModified', desc: true }]}
      />

      {data && (
        <p className="text-[12px] text-(--color-ink-muted)">
          Showing {items.length} of {data.totalCount}.
        </p>
      )}
    </div>
  );
}

function Signals({
  openConcernCount,
  prefillStatus,
}: {
  openConcernCount: number;
  prefillStatus: string | null;
}) {
  const prefillBad = prefillStatus && prefillStatus !== 'Ok';
  if (openConcernCount === 0 && !prefillBad) {
    return <span className="text-[12px] text-(--color-ink-muted)">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      {openConcernCount > 0 && (
        <span
          title={`${openConcernCount} open agent concern${openConcernCount === 1 ? '' : 's'}`}
          className="inline-flex items-center gap-1 rounded-(--radius-sm) bg-(--color-ochre-soft) px-1.5 py-0.5 text-[11px] font-semibold text-(--color-ochre)"
        >
          <AlertCircle className="size-3" />
          {openConcernCount}
        </span>
      )}
      {prefillBad && (
        <span
          title={`Latest prefill: ${prefillStatus}`}
          className="inline-flex items-center gap-1 rounded-(--radius-sm) bg-(--color-maroon-soft) px-1.5 py-0.5 text-[11px] font-semibold text-(--color-maroon)"
        >
          <Cloud className="size-3" />
          {prefillStatus}
        </span>
      )}
    </div>
  );
}

function prettyStatus(s: string): string {
  switch (s) {
    case 'draft':
      return 'Draft';
    case 'lodged':
      return 'Lodged';
    case 'lodgefailed':
      return 'Lodge failed';
    default:
      return s;
  }
}

function ReturnStatusPill({ status }: { status: string }) {
  // Draft is a normal state, not a warning — neutral. Ochre stays for genuine warnings.
  const tone = status === 'draft' ? 'neutral' : status === 'lodged' ? 'success' : 'danger';
  return <StatusPill tone={tone}>{prettyStatus(status)}</StatusPill>;
}
